import React from 'react';
import { ViewStyle, TextStyle, ScrollView, Platform } from 'react-native';
import styled from '@emotion/native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Button from '@/shared/components/Button';
import { LinkText, Link } from './styledComponents';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { ROUTES } from '@/shared/constants/routes';
import { H3, BodyText } from '@/shared/components/StyledText';
import { green, lightGreen, sand } from '@/shared/styles/colors';

export type SectionLayoutProps = {
  image?: React.ReactNode;
  heading: string;
  description: string;
  children: React.ReactNode;
  buttonText: string | null;
  onButtonPress: () => void;
  isButtonDisabled?: boolean;
  isButtonLoading?: boolean;
  footer?: React.ReactNode;
  containerStyle?: ViewStyle;
  contentStyle?: ViewStyle;
  descriptionStyle?: TextStyle;
  titleStyle?: TextStyle;
  titleMarginBottom?: number;
  descriptionMarginBottom?: number;
  buttonAfterDescription?: boolean;
  isStep?: boolean;
};

const SectionLayout: React.FC<SectionLayoutProps> = ({
  image,
  heading,
  description,
  children,
  buttonText,
  onButtonPress,
  isButtonDisabled = true,
  isButtonLoading = false,
  footer,
  containerStyle,
  contentStyle,
  descriptionStyle,
  titleStyle,
  titleMarginBottom = 14,
  descriptionMarginBottom = 0,
  buttonAfterDescription = false,
  isStep = true,
}) => {
  const { isDesktop } = useBreakpoint();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const defaultFooter = (
    <LinkText>
      Have Questions?{' '}
      <Link
        onPress={() => router.push(ROUTES.HELP.path)}
        accessibilityRole="link"
        accessible
      >
        Visit Help Center
      </Link>
    </LinkText>
  );

  const showStickyButtonOnMobile =
    buttonAfterDescription && !!buttonText && !isDesktop;

  if (showStickyButtonOnMobile) {
    const stickyBarPadding = Math.ceil(Math.max(insets.bottom, 8) / 2);
    const barHeight = stickyBarPadding + 52 + stickyBarPadding;
    const scrollContentPaddingBottom = barHeight;

    const isWeb = Platform.OS === 'web';

    const stickyBarContent = (
      <StickyButtonBar isWeb={isWeb} stickyBarBottomPadding={stickyBarPadding}>
        <Button
          text={buttonText}
          onPress={onButtonPress}
          disabled={isButtonDisabled}
          loading={isButtonLoading}
        />
      </StickyButtonBar>
    );
    // Use platform-specific helper so native bundle never loads react-dom
    const renderStickyBar = require('./sectionLayoutStickyBar').default;
    const stickyBar =
      isWeb && typeof document !== 'undefined'
        ? renderStickyBar(stickyBarContent)
        : stickyBarContent;

    return (
      <>
        <Container style={containerStyle}>
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{
              width: '100%',
              paddingBottom: scrollContentPaddingBottom,
              flexGrow: 0,
            }}
            showsVerticalScrollIndicator={true}
            keyboardShouldPersistTaps="handled"
          >
            <ContentContainer style={[contentStyle, { flex: 0, flexGrow: 0 }]}>
              <GroupedContent
                isDesktop={isDesktop}
                isStep={false}
                isScrollContent
              >
                <StepMainBlock isDesktop={isDesktop}>
                  <HeaderSection>
                    {image && <ImageContainer>{image}</ImageContainer>}
                    <StepTitle
                      style={[titleStyle, { marginBottom: titleMarginBottom }]}
                    >
                      {heading}
                    </StepTitle>
                    <Description
                      style={[
                        descriptionStyle,
                        { marginBottom: descriptionMarginBottom },
                      ]}
                    >
                      {description}
                    </Description>
                  </HeaderSection>
                </StepMainBlock>
                <DynamicContent>{children}</DynamicContent>
              </GroupedContent>
            </ContentContainer>
            <StickyLayoutFooter>{footer || defaultFooter}</StickyLayoutFooter>
          </ScrollView>
          {!isWeb ? stickyBarContent : null}
        </Container>
        {isWeb ? stickyBar : null}
      </>
    );
  }

  const mainCtaBlock = (
    <StepMainBlock isDesktop={isDesktop}>
      <HeaderSection>
        {image && <ImageContainer>{image}</ImageContainer>}
        <StepTitle style={[titleStyle, { marginBottom: titleMarginBottom }]}>
          {heading}
        </StepTitle>
        <Description
          style={[descriptionStyle, { marginBottom: descriptionMarginBottom }]}
        >
          {description}
        </Description>
        {buttonAfterDescription && buttonText ? (
          <ButtonSection style={{ marginBottom: isDesktop ? 32 : 24 }}>
            <Button
              text={buttonText}
              onPress={onButtonPress}
              disabled={isButtonDisabled}
              loading={isButtonLoading}
            />
          </ButtonSection>
        ) : null}
      </HeaderSection>
      {!buttonAfterDescription && buttonText ? (
        <ButtonSection>
          <Button
            text={buttonText}
            onPress={onButtonPress}
            disabled={isButtonDisabled}
            loading={isButtonLoading}
          />
        </ButtonSection>
      ) : null}
    </StepMainBlock>
  );

  return (
    <Container style={containerStyle}>
      <ContentContainer style={contentStyle}>
        <GroupedContent isDesktop={isDesktop} isStep={isStep}>
          {mainCtaBlock}
          <DynamicContent>{children}</DynamicContent>
        </GroupedContent>
      </ContentContainer>
      <FooterSection>{footer || defaultFooter}</FooterSection>
    </Container>
  );
};

export default SectionLayout;

const Container = styled.View({
  flex: 1,
  alignItems: 'stretch',
  paddingHorizontal: 30,
  width: '100%',
});

const ImageContainer = styled.View({
  marginBottom: 40,
});

const ContentContainer = styled.View({
  width: '100%',
  maxWidth: 548,
  alignSelf: 'center',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
});

const StepMainBlock = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  alignItems: 'center',
  ...(isDesktop
    ? { width: '100%', maxWidth: 548 }
    : { width: '100%', maxWidth: 302, alignSelf: 'center' }),
}));

const GroupedContent = styled.View<{
  isDesktop: boolean;
  isStep: boolean;
  isScrollContent?: boolean;
}>(({ isDesktop, isStep, isScrollContent }) => ({
  ...(!isDesktop &&
    isStep &&
    !isScrollContent && {
      flex: 1,
    }),
  alignItems: 'center',
  justifyContent: isScrollContent ? 'flex-start' : 'center',
  width: '100%',
}));

const HeaderSection = styled.View({
  marginHorizontal: 'auto',
  alignItems: 'center',
});

export const StepTitle = styled(H3)({
  marginBottom: 14,
  color: green,
  textAlign: 'center',
  textTransform: 'uppercase',
});

export const Description = styled(BodyText)({
  textAlign: 'center',
  color: lightGreen,
  fontSize: 16,
});

const DynamicContent = styled.View({
  alignItems: 'center',
  width: '100%',
});

const ButtonSection = styled.View({
  marginVertical: 16,
  paddingBottom: 24,
  width: '100%',
  alignItems: 'center',
  maxWidth: 302,
  marginHorizontal: 'auto',
});

const StickyButtonBar = styled.View<{
  isWeb?: boolean;
  stickyBarBottomPadding?: number;
}>(({ isWeb, stickyBarBottomPadding = 0 }) => ({
  width: '100%',
  paddingTop: stickyBarBottomPadding,
  paddingBottom: stickyBarBottomPadding,
  paddingHorizontal: 24,
  alignItems: 'center',
  maxWidth: 548,
  alignSelf: 'center',
  backgroundColor: sand,
  marginHorizontal: 'auto',
  ...(isWeb !== undefined && {
    position: isWeb ? ('fixed' as const) : 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  }),
}));

const FooterSection = styled.View({
  marginTop: 24,
});

const StickyLayoutFooter = styled.View({
  marginTop: 8,
  marginBottom: 16,
});
